import type { AdminInterFace } from "../../Types";
import { getDisplayRole } from "./RoleDisplayHelper";

/** Portal roles for classic masjid / musali admins (not RBAC feature delegates). */
const CLASSIC_EXTERNAL_ROLES = new Set([
  "subadmin",
  "musaliadmin",
  "masjidadmin",
]);

/** Minimal fields needed to detect delegated Sub-Admin (entity assignedUser, admin rows). */
export type DelegatedSubAdminCheckInput = Pick<
  AdminInterFace,
  "role" | "permissionRole" | "permissions" | "ownershipType"
>;

/**
 * External “Sub- Admin” tab: masjid-scoped delegated admins (invite/RBAC),
 * e.g. event_admin, tv_admin, custom_* — excluding primary Masjid / Musali admins.
 */
export function isDelegatedSubAdmin(admin: DelegatedSubAdminCheckInput): boolean {
  const ownership = admin.ownershipType;
  if (ownership === "ORGANIZATION" || ownership === "INDIVIDUAL") {
    return false;
  }

  const role = (admin.role || "").toLowerCase().trim();
  if (!role || role === "user") {
    return false;
  }
  if (CLASSIC_EXTERNAL_ROLES.has(role)) {
    return false;
  }

  const pr = (admin.permissionRole || "").toLowerCase().trim();
  if (pr && !CLASSIC_EXTERNAL_ROLES.has(pr)) {
    if (pr.startsWith("custom_") || pr.endsWith("_admin")) {
      return true;
    }
  }

  if (role.startsWith("custom_")) {
    return true;
  }
  if (role.endsWith("_admin")) {
    return true;
  }

  const perms = admin.permissions;
  if (Array.isArray(perms) && perms.length > 0) {
    return true;
  }

  return false;
}

/** Role label in MAP UI for delegated Sub-Admin tab/modal (data `role` unchanged). */
export const DELEGATED_SUB_ADMIN_LABEL = "Sub-Admin";

export function getExternalAdminRoleLabel(admin: AdminInterFace): string {
  if (isDelegatedSubAdmin(admin)) {
    return DELEGATED_SUB_ADMIN_LABEL;
  }
  return getDisplayRole(admin.role ?? "", admin.ownershipType ?? "");
}

/** UI label for stored roles; strips multi-feature `custom_` prefix (data unchanged). */
export function formatRoleStringForDisplay(raw: string): string {
  let s = raw.trim();
  if (!s) {
    return s;
  }
  if (s.toLowerCase().startsWith("custom_")) {
    s = s.slice(7);
  }
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Short label for delegated RBAC role (permissionRole or synthetic from role). */
export function formatRbacRoleLabel(admin: AdminInterFace): string {
  const raw = (admin.permissionRole || admin.role || "").trim();
  if (!raw) {
    return "—";
  }
  return formatRoleStringForDisplay(raw);
}

/**
 * One-line summary for permissions array; full list for tooltips.
 */
export function summarizePermissions(
  permissions?: string[] | null
): { summary: string; full: string } {
  if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
    return { summary: "—", full: "" };
  }
  if (permissions.includes("*")) {
    return { summary: "Full access (*)", full: "*" };
  }
  const prefixes = new Set<string>();
  for (const p of permissions) {
    const [prefix] = String(p || "").split(".");
    if (prefix) {
      prefixes.add(prefix);
    }
  }
  const list = [...prefixes].sort();
  const full = permissions.join(", ");
  if (list.length === 0) {
    return { summary: `${permissions.length} permission(s)`, full };
  }
  if (list.length <= 3) {
    return { summary: list.join(", "), full };
  }
  return { summary: `${list.slice(0, 3).join(", ")} +${list.length - 3}`, full };
}
